import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  getAdminTokenUsage,
  getAdminTokenUsageDrilldown,
  adminGrantCredits,
  TokenBalance,
} from '../../services/api';

// ── Styles ────────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  padding: 24px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? '#2563eb' : '#6b7280')};
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? '#2563eb' : 'transparent')};
  background: none;
  cursor: pointer;
  margin-bottom: -1px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
  vertical-align: middle;
`;

const DrillBtn = styled.button`
  padding: 4px 10px;
  font-size: 0.75rem;
  color: #2563eb;
  background: none;
  border: 1px solid #2563eb;
  border-radius: 4px;
  cursor: pointer;
`;

const GrantBtn = styled.button`
  padding: 4px 10px;
  font-size: 0.75rem;
  color: #ffffff;
  background: #16a34a;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 6px;
`;

const PctBar = styled.div<{ $pct: number }>`
  width: 80px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
  &::after {
    content: '';
    display: block;
    width: ${(p) => Math.min(p.$pct, 100)}%;
    height: 100%;
    background: ${(p) => (p.$pct >= 80 ? '#dc2626' : '#2563eb')};
  }
`;

const SectionTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 12px 0;
`;

const BackBtn = styled.button`
  margin-bottom: 16px;
  font-size: 0.8125rem;
  color: #2563eb;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const ErrorMsg = styled.div`
  color: #dc2626;
  font-size: 0.8125rem;
  margin: 8px 0;
`;

// ── Grant Credits Modal ───────────────────────────────────────────────────────

interface GrantTarget { user_id?: number; group_id?: number; label: string }

interface GrantModalProps {
  target: GrantTarget;
  onClose: () => void;
  onSuccess: () => void;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 380px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;

const ModalTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8125rem;
  color: #374151;
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  box-sizing: border-box;
  margin-bottom: 12px;
`;

const ModalBtnRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const CancelBtn = styled.button`
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 0.875rem;
`;

const ConfirmBtn = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #16a34a;
  color: #fff;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
`;

const GrantModal: React.FC<GrantModalProps> = ({ target, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!amount || isNaN(parseFloat(amount))) { setErr('Enter a valid amount.'); return; }
    setLoading(true);
    setErr('');
    try {
      await adminGrantCredits({
        user_id: target.user_id,
        group_id: target.group_id,
        amount_usd: amount,
        note,
      });
      onSuccess();
    } catch (e: any) {
      setErr(e?.message || 'Grant failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Grant Credits — {target.label}</ModalTitle>
        <Label>Amount (USD)</Label>
        <Input
          type="number"
          min="0.01"
          step="0.01"
          placeholder="10.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Label>Note (optional)</Label>
        <Input
          type="text"
          placeholder="e.g. Dev testing, demo account"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {err && <ErrorMsg>{err}</ErrorMsg>}
        <ModalBtnRow>
          <CancelBtn onClick={onClose}>Cancel</CancelBtn>
          <ConfirmBtn onClick={submit} disabled={loading}>
            {loading ? 'Granting...' : 'Grant Credits'}
          </ConfirmBtn>
        </ModalBtnRow>
      </Modal>
    </Overlay>
  );
};

// ── Balance helpers ───────────────────────────────────────────────────────────

function fmtUsd(v: string | undefined) {
  if (!v) return '$0.00';
  return `$${parseFloat(v).toFixed(2)}`;
}

function BalanceSummary({ bal }: { bal: TokenBalance }) {
  return (
    <span>
      <PctBar $pct={bal.percent_used} />
      {fmtUsd(bal.included_used_usd)} / {fmtUsd(bal.included_usd)} included
      {' · '}topup: {fmtUsd(bal.topup_balance_usd)}
    </span>
  );
}

// ── Drilldown View ────────────────────────────────────────────────────────────

interface DrilldownProps {
  userId?: number;
  groupId?: number;
  label: string;
  onBack: () => void;
}

const DrilldownView: React.FC<DrilldownProps> = ({ userId, groupId, label, onBack }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof getAdminTokenUsageDrilldown>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAdminTokenUsageDrilldown({ user_id: userId, group_id: groupId })
      .then(setData)
      .finally(() => setLoading(false));
  }, [userId, groupId]);

  return (
    <div>
      <BackBtn onClick={onBack}>← Back</BackBtn>
      <SectionTitle>{label} — Usage Detail</SectionTitle>
      {loading && <div>Loading...</div>}
      {data && (
        <>
          <div style={{ marginBottom: 16, fontSize: '0.8125rem', color: '#374151' }}>
            Total cost: <strong>{fmtUsd(data.total_cost_usd)}</strong>
            {' · '}
            <BalanceSummary bal={data.balance} />
          </div>
          {data.by_user && data.by_user.length > 0 && (
            <>
              <SectionTitle style={{ fontSize: '0.875rem' }}>By Sub-Account</SectionTitle>
              <Table>
                <thead>
                  <tr><Th>User</Th><Th>Cost (USD)</Th><Th>Calls</Th></tr>
                </thead>
                <tbody>
                  {data.by_user.map((u, i) => (
                    <tr key={u.user_id ?? i}>
                      <Td>{u.email}</Td>
                      <Td>{fmtUsd(u.cost_usd)}</Td>
                      <Td>{u.calls}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <br />
            </>
          )}
          <SectionTitle style={{ fontSize: '0.875rem' }}>By Source</SectionTitle>
          <Table>
            <thead>
              <tr><Th>Source</Th><Th>Cost (USD)</Th><Th>Calls</Th></tr>
            </thead>
            <tbody>
              {Object.entries(data.by_source).map(([src, d]) => (
                <tr key={src}>
                  <Td>{src}</Td>
                  <Td>{fmtUsd(d.cost_usd)}</Td>
                  <Td>{d.calls}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <br />
          <SectionTitle style={{ fontSize: '0.875rem' }}>Recent Records</SectionTitle>
          <Table>
            <thead>
              <tr><Th>Time</Th><Th>Source</Th><Th>Step</Th><Th>Tokens In</Th><Th>Tokens Out</Th><Th>Cost</Th></tr>
            </thead>
            <tbody>
              {data.records.map((r) => (
                <tr key={r.id}>
                  <Td>{new Date(r.created_at).toLocaleString()}</Td>
                  <Td>{r.source}</Td>
                  <Td>{r.step_name || '—'}</Td>
                  <Td>{r.input_tokens ?? '—'}</Td>
                  <Td>{r.output_tokens ?? '—'}</Td>
                  <Td>{fmtUsd(r.cost_usd)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const AdminTokenUsage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'groups' | 'users'>('groups');
  const [data, setData] = useState<Awaited<ReturnType<typeof getAdminTokenUsage>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState<DrilldownProps | null>(null);
  const [grantTarget, setGrantTarget] = useState<GrantTarget | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    getAdminTokenUsage().then(setData).finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (drilldown) {
    return (
      <Wrap>
        <DrilldownView
          userId={drilldown.userId}
          groupId={drilldown.groupId}
          label={drilldown.label}
          onBack={() => setDrilldown(null)}
        />
        {grantTarget && (
          <GrantModal
            target={grantTarget}
            onClose={() => setGrantTarget(null)}
            onSuccess={() => { setGrantTarget(null); reload(); }}
          />
        )}
      </Wrap>
    );
  }

  return (
    <Wrap>
      <TabRow>
        <Tab $active={activeTab === 'groups'} onClick={() => setActiveTab('groups')}>By Group</Tab>
        <Tab $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>By User</Tab>
      </TabRow>

      {loading && <div>Loading...</div>}

      {!loading && data && activeTab === 'groups' && (
        <>
          <SectionTitle>Group Balances</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Group</Th>
                <Th>Allowance</Th>
                <Th>30d Cost</Th>
                <Th>30d Calls</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => {
                const groupLabel = g.group_name ? `${g.group_name} (#${g.group_id})` : `Group #${g.group_id}`;
                return (
                  <tr key={g.group_id}>
                    <Td>{groupLabel}</Td>
                    <Td><BalanceSummary bal={g.balance} /></Td>
                    <Td>{fmtUsd(g.usage_30d_usd)}</Td>
                    <Td>{g.usage_30d_calls}</Td>
                    <Td>
                      <DrillBtn onClick={() => setDrilldown({ groupId: g.group_id, label: groupLabel, onBack: () => setDrilldown(null) })}>
                        Drill down
                      </DrillBtn>
                      <GrantBtn onClick={() => setGrantTarget({ group_id: g.group_id, label: groupLabel })}>
                        Grant
                      </GrantBtn>
                    </Td>
                  </tr>
                );
              })}
              {data.groups.length === 0 && (
                <tr><Td colSpan={5} style={{ color: '#9ca3af' }}>No group balances yet.</Td></tr>
              )}
            </tbody>
          </Table>
        </>
      )}

      {!loading && data && activeTab === 'users' && (
        <>
          <SectionTitle>All Users</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>User</Th>
                <Th>Group</Th>
                <Th>30d Cost</Th>
                <Th>30d Calls</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => {
                const userLabel = u.email;
                const groupLabel = u.group_name
                  ? `${u.group_name} (#${u.group_id})`
                  : u.group_id ? `Group #${u.group_id}` : '—';
                return (
                  <tr key={u.user_id}>
                    <Td>{userLabel}</Td>
                    <Td style={{ color: '#6b7280', fontSize: '0.8rem' }}>{groupLabel}</Td>
                    <Td>{fmtUsd(u.usage_30d_usd)}</Td>
                    <Td>{u.usage_30d_calls}</Td>
                    <Td>
                      <DrillBtn onClick={() => setDrilldown({ userId: u.user_id, label: userLabel, onBack: () => setDrilldown(null) })}>
                        Drill down
                      </DrillBtn>
                    </Td>
                  </tr>
                );
              })}
              {data.users.length === 0 && (
                <tr><Td colSpan={5} style={{ color: '#9ca3af' }}>No usage records yet.</Td></tr>
              )}
            </tbody>
          </Table>
        </>
      )}

      {grantTarget && (
        <GrantModal
          target={grantTarget}
          onClose={() => setGrantTarget(null)}
          onSuccess={() => { setGrantTarget(null); reload(); }}
        />
      )}
    </Wrap>
  );
};

export default AdminTokenUsage;
